import { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Lock, EyeOff, Eye, KeyRound, X } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogTrigger,
  AlertDialogDescription,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useChangeOwnPassword, useChangeUserPassword } from '../hooks';

// Schema factories
const createOwnPwSchema = (t: TFunction) =>
  z
    .object({
      oldPassword: z.string().min(1, t('oldPasswordRequired')),
      newPassword: z.string().min(8, t('passwordMin8')),
      confirmNewPassword: z.string().min(1, t('confirmPasswordRequired')),
    })
    .refine((v) => v.newPassword === v.confirmNewPassword, {
      message: t('passwordsMustMatch'),
      path: ['confirmNewPassword'],
    });

const createOtherPwSchema = (t: TFunction) =>
  z
    .object({
      newPassword: z.string().min(8, t('passwordMin8')),
      confirmNewPassword: z.string().min(1, t('confirmPasswordRequired')),
    })
    .refine((v) => v.newPassword === v.confirmNewPassword, {
      message: t('passwordsMustMatch'),
      path: ['confirmNewPassword'],
    });

type OwnPwFormValues = z.infer<ReturnType<typeof createOwnPwSchema>>;
type OtherPwFormValues = z.infer<ReturnType<typeof createOtherPwSchema>>;

// Password input with eye toggle
function PasswordInput({
  value,
  onChange,
  placeholder,
  name,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  name: string;
}) {
  const [show, setShow] = useState(false);

  return (
    <div className="relative">
      <Input
        name={name}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        type={show ? 'text' : 'password'}
        placeholder={placeholder}
        className="pr-10"
      />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6"
        onClick={() => setShow((s) => !s)}
        tabIndex={-1}
        aria-label={show ? 'Hide password' : 'Show password'}
      >
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </Button>
    </div>
  );
}

interface ChangePasswordDialogProps {
  id?: number | string;
  isSelf?: boolean;
  trigger?: React.ReactNode;
}

export function ChangePasswordDialog({
  id,
  isSelf = false,
  trigger,
}: ChangePasswordDialogProps) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const changeOwnPasswordMutation = useChangeOwnPassword();
  const changeUserPasswordMutation = useChangeUserPassword();

  const schema = useMemo(
    () => (isSelf ? createOwnPwSchema(t) : createOtherPwSchema(t)),
    [isSelf, t]
  );

  const form = useForm<OwnPwFormValues | OtherPwFormValues>({
    resolver: zodResolver(schema),
    defaultValues: isSelf
      ? { oldPassword: '', newPassword: '', confirmNewPassword: '' }
      : { newPassword: '', confirmNewPassword: '' },
  });

  const { isSubmitting } = form.formState;
  const isPending = changeOwnPasswordMutation.isPending || changeUserPasswordMutation.isPending;

  const handleSubmit = () => {
    if (isSelf) {
      const values = form.getValues() as OwnPwFormValues;
      changeOwnPasswordMutation.mutate(
        { oldPassword: values.oldPassword, newPassword: values.newPassword },
        {
          onSuccess: () => {
            form.reset();
            setOpen(false);
          },
        }
      );
    } else {
      if (!id) return;
      const values = form.getValues() as OtherPwFormValues;
      changeUserPasswordMutation.mutate(
        { id, data: { newPassword: values.newPassword } },
        {
          onSuccess: () => {
            form.reset();
            setOpen(false);
          },
        }
      );
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        {trigger ? (
          trigger
        ) : (
          <Button size="sm" variant="outline">
            <KeyRound className="h-4 w-4 mr-1" />
            {t('changePassword')}
          </Button>
        )}
      </AlertDialogTrigger>

      <AlertDialogContent className="p-6 min-w-[28rem] max-h-[80%] overflow-y-auto">
        <div className="flex items-center justify-between border-b pb-3">
          <AlertDialogTitle className="flex items-center gap-2">
            <Lock className="h-4 w-4" />
            {t('changePassword')}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <button onClick={() => setOpen(false)} aria-label="Close dialog">
              <X size={20} />
            </button>
          </AlertDialogDescription>
        </div>

        <Form {...form}>
          <form className="mt-4 grid grid-cols-1 gap-4" noValidate>
            {isSelf && (
              <FormField
                control={form.control}
                name="oldPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('oldPassword')}</FormLabel>
                    <FormControl>
                      <PasswordInput
                        value={field.value as string}
                        onChange={field.onChange}
                        placeholder={t('enterOldPassword')}
                        name="oldPassword"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="newPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('newPassword')}</FormLabel>
                  <FormControl>
                    <PasswordInput
                      value={field.value}
                      onChange={field.onChange}
                      placeholder={t('enterNewPassword')}
                      name="newPassword"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="confirmNewPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('confirmNewPassword')}</FormLabel>
                  <FormControl>
                    <PasswordInput
                      value={field.value}
                      onChange={field.onChange}
                      placeholder={t('confirmNewPassword')}
                      name="confirmNewPassword"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2 mt-2">
              <AlertDialogCancel type="button">{t('cancel')}</AlertDialogCancel>
              <Button
                type="button"
                disabled={isSubmitting || isPending}
                onClick={form.handleSubmit(handleSubmit)}
              >
                {isSubmitting || isPending ? t('saving') : t('save')}
              </Button>
            </div>
          </form>
        </Form>

        <AlertDialogFooter className="mt-0" />
      </AlertDialogContent>
    </AlertDialog>
  );
}
